import { Box } from '@mui/material';
import { useIntl } from 'react-intl';
import type { ClinicalPeopleList } from '~/backend/connector-next-api/connector-next-schemas';
import { putAssignment } from '~/backend/connector-next-api/patientWritesClient';
import { enqueueSnackbar } from '~/utils/notifications';
import { usePatientWrite } from '../hooks';
import { personLabel } from '../model';
import ChoicePicker from './ChoicePicker';
import { personChoices } from './personChoices';

/**
 * Assigns the patient to anybody who can open patients, or to nobody, whoever
 * holds it now, guarded by the patient's version. For a user permitted to
 * assign patients. A refusal is told in a snackbar, as the assignment button
 * tells one, because the refreshed lists can move the patient off the one
 * showing it.
 */
export default function AssigneePicker({
    patient,
    people,
}: {
    patient: {
        id?: string | null;
        version?: string | null;
        assignedToUserId?: string | null;
    };
    people: ClinicalPeopleList | undefined;
}) {
    const intl = useIntl();
    const { write, canWrite, isSaving } = usePatientWrite(
        patient,
        (userId: string | undefined, id, version) =>
            putAssignment(id, userId ?? null, version),
        () =>
            enqueueSnackbar(
                intl.formatMessage({ id: 'patientList.tasks.saveFailed' }),
                { variant: 'error' }
            )
    );
    const assignedTo = patient.assignedToUserId ?? undefined;

    return (
        // The menu renders in a portal, and its clicks still bubble through
        // React to a row that opens the patient on click.
        <Box
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            sx={{ minWidth: 0, width: '100%' }}
        >
            <ChoicePicker
                name={intl.formatMessage({ id: 'patientList.assignee' })}
                choices={personChoices(people?.people)}
                value={assignedTo}
                placeholder={intl.formatMessage({
                    id: 'patientList.assignee.unassigned',
                })}
                unknownLabel={
                    assignedTo && personLabel(intl, people, assignedTo)
                }
                disabled={isSaving || !canWrite}
                onChange={(userId) => write(userId)}
            />
        </Box>
    );
}
